<template>
  <div class="w-full">
    <!-- Main Navbar -->
    <div class="navbar bg-base-100 dark:bg-neutral text-base-content dark:text-neutral-content shadow-md fixed top-0 z-50 transition-all duration-300">
      <div class="container mx-auto px-2">
        <!-- Mobile Nav & Sidebar Button -->
        <div class="flex-none lg:hidden" v-if="menu">
          <label for="my-drawer-2" class="btn btn-square btn-ghost relative">
            <span class="absolute top-1 right-1 flex h-2 w-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="w-6 h-6 stroke-current">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </label>
        </div>

        <!-- Logo Section -->
        <div class="flex-1">
          <router-link to="/" class="flex items-center gap-2 transition-transform hover:scale-105 duration-300">
            <div class="w-8 h-8 sm:w-10 sm:h-10 bg-primary/20 rounded-xl flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 sm:w-6 sm:h-6 text-primary">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </div>
            <span class="text-lg font-bold inline">takkip.com</span>
          </router-link>
        </div>

        <!-- Actions Section -->
        <div class="flex-none flex items-center gap-1 sm:gap-2">
          <!-- Theme Toggle -->
          <div class="mr-0 sm:mr-1">
            <ThemeToggle />
          </div>

          <!-- Authentication -->
          <div v-if="!isAuth" class="flex gap-1">
            <router-link to="/users/login" class="btn btn-ghost btn-sm rounded-lg">
              <UserCircleIcon class="h-5 w-5 sm:mr-1" />
              <span class="hidden sm:inline">Giriş Yap</span>
            </router-link>
            <router-link to="/users/signIn" class="btn btn-primary btn-sm rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <span class="hidden sm:inline">Kayıt Ol</span>
            </router-link>
          </div>

          <!-- User Dropdown -->
          <div v-if="isAuth" class="dropdown dropdown-end">
            <label tabindex="0" class="btn btn-ghost btn-sm rounded-lg gap-1 sm:gap-2 pr-1 sm:pr-4 cursor-pointer">
              <div class="avatar placeholder online">
                <div class="bg-primary text-primary-content rounded-full w-7 sm:w-8">
                  <span class="text-xs sm:text-sm">{{ userInitials }}</span>
                </div>
              </div>
              <span class="hidden md:inline truncate max-w-[120px]">{{ user?.name }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </label>
            <ul tabindex="0" class="dropdown-content menu shadow-lg bg-base-100 dark:bg-neutral rounded-box w-64 mt-2 p-2 z-50">
              <div class="px-4 py-3 border-b border-base-300 dark:border-neutral-700">
                <div class="flex items-center gap-3">
                  <div class="avatar placeholder">
                    <div class="bg-primary text-primary-content rounded-full w-10">
                      <span>{{ userInitials }}</span>
                    </div>
                  </div>
                  <div>
                    <p class="font-medium">{{ user?.name }} {{ user?.username }}</p>
                    <p class="text-xs text-base-content/70 dark:text-neutral-content/70 truncate">{{ user?.email }}</p>
                  </div>
                </div>
              </div>
              <li class="menu-title mt-2">
                <span>Hesabım</span>
              </li>
              <li>
                <router-link :to="`/users/detail/${user?.id}`" class="flex items-center p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <div>
                    <div class="font-medium">Profilim</div>
                    <div class="text-xs opacity-60">Profil bilgilerini görüntüle</div>
                  </div>
                </router-link>
              </li>
              <li>
                <router-link :to="`/users/update/${user?.id}`" class="flex items-center p-3">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                  </svg>
                  <div>
                    <div class="font-medium">Profili Düzenle</div>
                    <div class="text-xs opacity-60">Bilgilerini güncelle</div>
                  </div>
                </router-link>
              </li>
  
              <div class="divider my-1"></div>
              <li>
                <a @click="logOutFNC" class="flex items-center p-3 text-error hover:bg-error/10">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
                  </svg>
                  <div>
                    <div class="font-medium">Çıkış Yap</div>
                    <div class="text-xs opacity-60">Hesabından çıkış yap</div>
                  </div>
                </a>
              </li>

            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Menu Dropdown Button (for pages without sidebar) -->
    <div class="lg:hidden fixed bottom-4 right-4 z-40" v-if="!menu && isAuth">
      <button class="btn btn-primary shadow-lg btn-circle flex items-center justify-center" onclick="document.getElementById('mobile-menu-modal').showModal()">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
    </div>

    <!-- Mobile Menu Modal -->
    <dialog id="mobile-menu-modal" class="modal modal-bottom sm:modal-middle">
      <div class="modal-box bg-base-100 dark:bg-neutral">
        <!-- User Info in Modal -->
        <div class="flex items-center space-x-3 border-b pb-4 mb-4 border-base-300 dark:border-neutral-700" v-if="isAuth">
          <div class="avatar placeholder">
            <div class="bg-primary text-primary-content rounded-full w-12">
              <span class="text-lg">{{ userInitials }}</span>
            </div>
          </div>
          <div>
            <div class="font-bold">{{ user?.name }} {{ user?.username }}</div>
            <div class="text-sm opacity-60">{{ user?.email || 'Kullanıcı' }}</div>
          </div>
        </div>
        
        <h3 class="font-bold text-lg mb-4">Menü</h3>
        <div class="py-2">
          <ul class="menu bg-base-100 dark:bg-neutral rounded-box w-full gap-1">
            <li>
              <router-link to="/" class="flex items-center py-3" @click="closeModal">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Ana Sayfa
              </router-link>
            </li>
            <li>
              <router-link to="/tasks" class="flex items-center py-3" @click="closeModal">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                Görevlerim
              </router-link>
            </li>
            <li>
              <router-link to="/kanban" class="flex items-center py-3" @click="closeModal">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Kanban
              </router-link>
            </li>
            <li>
              <router-link to="/calendar" class="flex items-center py-3" @click="closeModal">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
                Takvim
              </router-link>
            </li>
            <li v-if="isAuth">
              <router-link :to="`/users/detail/${user?.id}`" class="flex items-center py-3" @click="closeModal">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Profilim
              </router-link>
            </li>
          </ul>
        </div>
        <div class="flex flex-col gap-2 mt-4 pt-4 border-t border-base-300 dark:border-neutral-700">
          <div class="flex justify-between items-center">
            <span class="font-medium">Tema</span>
            <ThemeToggle />
          </div>
          <button @click="logOutFNC" v-if="isAuth" class="btn btn-outline btn-error btn-sm mt-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
            </svg>
            Çıkış Yap
          </button>

        </div>
        <div class="modal-action">
          <form method="dialog">
            <button class="btn">Kapat</button>
          </form>
        </div>
      </div>
    </dialog>

    <!-- Spacer to prevent content from being hidden under fixed header -->
    <div class="h-16"></div>
  </div>
</template>

<script setup>
import { getCurrentInstance, ref, computed, defineProps, onMounted, watch, onUnmounted } from "vue";
import ThemeToggle from "../ThemeToggle.vue";
import { UserCircleIcon } from "@heroicons/vue/24/solid";
import { useRouter, useRoute } from "vue-router";
import  useModelUsers from "../../compositions/useModelUsers"
import pb from "../../services/pocketbase";

const app = getCurrentInstance();
const store = app.appContext.config.globalProperties.$store;
const props = defineProps(["menu"]);
const router = useRouter();
const route = useRoute();
const { logOut } = useModelUsers();
const isAuth = ref(false);

// Oturum durumunu kontrol et ve güncelle
const checkAuth = () => {
  isAuth.value = pb.authStore.isValid;
};



// Component mount olduğunda ilk kontrolü yap
onMounted(() => {
  checkAuth();
  

  // Her 60 saniyede bir oturum durumunu kontrol et
  const authCheckInterval = setInterval(checkAuth, 60000);
  
  // Component unmount olduğunda interval'i temizle
  onUnmounted(() => {
    clearInterval(authCheckInterval);
  });



  if(!store.getters._isAuthenticated || !pb.authStore.isValid){
    logOutFNC();
  }

});

// Store'daki auth durumu değiştiğinde isAuth'u güncelle
watch(() => store.getters._isAuthenticated, (newValue) => {
  isAuth.value = newValue;
});

watch(() => isAuth, (newValue, oldValue) => {
  if(!newValue && oldValue){
    logOutFNC();
  }
});

const logOutFNC = () => {
  logOut().then(() => {
    store.commit("logoutUser");
    isAuth.value = false;
    router.push({name: "HomePage"})


    pocketbaseLogout();
  })
};

const pocketbaseLogout = () => {
  pb.authStore.clear();
  store.commit("logoutUser");
  isAuth.value = false;
  router.push({name: "HomePage"});
};

const closeModal = () => {
  document.getElementById('mobile-menu-modal').close();
};

const user = computed(() => {
  return store.getters._getCurrentUser;
});

const userInitials = computed(() => {
  if (!user.value) return '';
  return (user.value.name?.charAt(0) || '') + (user.value.username?.charAt(0) || '');
});
</script>

<style scoped>
.dropdown-content {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .navbar {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}
</style>
